import { Helmet } from "react-helmet-async";
import { Phone, Shield, CheckCircle, MapPin, Thermometer, Car, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import PPFQuoteForm from "@/components/PPFQuoteForm";
import xpelLogo from "@/assets/xpel-logo.png";

const PPFQuoteLanding = () => {
  const phoneNumber = "(702) 559-9365";
  const phoneHref = "tel:+17025599365";

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>PPF Las Vegas | Get Your XPEL Paint Protection Quote</title>
        <meta 
          name="description" 
          content="Professional XPEL paint protection film installation in Las Vegas. Shield your vehicle from UV, rock chips, and desert road debris. Get a free quote today." 
        />
        <link rel="canonical" href="https://battlebornppf.com/ppf-quote" />
        <meta name="robots" content="noindex, nofollow" />
        {/* Google Ads Conversion Tracking */}
        <script async src="https://www.googletagmanager.com/gtag/js?id=AW-17910601966"></script>
        <script>{`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'AW-17910601966');
        `}</script>
      </Helmet>

      {/* ═══════════════════════════════════════════════════════════════════
          ABOVE THE FOLD - Hero Section
          Purpose: Immediate value prop + primary conversion action
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="relative min-h-screen lg:min-h-[90vh] flex items-center py-8 lg:py-0">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-black via-zinc-900 to-black" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-accent/10 via-transparent to-transparent" />
        
        <div className="relative z-10 container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Column - Copy */}
            <div className="text-center lg:text-left">
              {/* Trust Badge */}
              <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2 mb-6">
                <img src={xpelLogo} alt="XPEL Certified" className="h-5 w-auto" />
                <span className="text-sm text-muted-foreground font-medium">Certified XPEL Installer</span>
              </div>
              
              {/* Primary Headline */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground leading-tight mb-4">
                Professional <span className="text-accent">XPEL Paint Protection</span> in Las Vegas
              </h1>
              
              {/* Subheadline - Vegas-specific */}
              <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-xl mx-auto lg:mx-0">
                Shield your vehicle from intense desert UV, I-15 rock chips, and extreme heat damage. Climate-controlled installation by certified professionals.
              </p>
              
              {/* Click-to-Call */}
              <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
                <a 
                  href={phoneHref}
                  className="inline-flex items-center gap-2 text-xl font-bold text-accent hover:text-accent/80 transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  {phoneNumber}
                </a>
                <span className="text-muted-foreground hidden sm:inline">or fill out the form →</span>
              </div>
            </div>
            
            {/* Right Column - Lead Form */}
            <div className="w-full max-w-md mx-auto lg:mx-0 lg:ml-auto">
              <div className="text-center mb-4">
                <h2 className="text-xl font-heading font-semibold text-foreground">
                  Get Your Free PPF Quote
                </h2>
                <p className="text-sm text-muted-foreground">
                  No obligation. Fast response guaranteed.
                </p>
              </div>
              <PPFQuoteForm variant="hero" />
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          TRUST & QUALIFICATION SECTION
          Purpose: Filter unqualified leads, establish credibility
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="py-16 bg-[#1A1A1A] border-y border-white/10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-center text-foreground mb-8">
              Professional Shop Installation Only
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* Qualification Points */}
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-foreground font-medium">Shop-Installed PPF Only</p>
                    <p className="text-sm text-muted-foreground">No mobile installs or DIY kits</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-foreground font-medium">Climate-Controlled Environment</p>
                    <p className="text-sm text-muted-foreground">Dust-free installation facility</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-foreground font-medium">XPEL Certified Installers</p>
                    <p className="text-sm text-muted-foreground">Factory-trained technicians</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-foreground font-medium">10-Year Manufacturer Warranty</p>
                    <p className="text-sm text-muted-foreground">Backed by XPEL</p>
                  </div>
                </div>
              </div>
              
              {/* Visual Placeholders */}
              <div className="grid grid-cols-2 gap-4">
                <div className="aspect-square bg-[#2A2A2A] rounded-lg border border-white/10 flex items-center justify-center">
                  <div className="text-center p-4">
                    <Shield className="w-8 h-8 text-accent mx-auto mb-2" />
                    <p className="text-xs text-muted-foreground">Shop Interior</p>
                  </div>
                </div>
                <div className="aspect-square bg-[#2A2A2A] rounded-lg border border-white/10 flex items-center justify-center">
                  <div className="text-center p-4">
                    <Car className="w-8 h-8 text-accent mx-auto mb-2" />
                    <p className="text-xs text-muted-foreground">Mid-Install</p>
                  </div>
                </div>
                <div className="col-span-2 aspect-video bg-[#2A2A2A] rounded-lg border border-white/10 flex items-center justify-center">
                  <div className="text-center p-4">
                    <Award className="w-8 h-8 text-accent mx-auto mb-2" />
                    <p className="text-xs text-muted-foreground">PPF Edge Detail</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          COVERAGE EDUCATION SECTION
          Purpose: Educate on options without pricing, drive quote requests
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
                PPF Coverage Options
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Choose the level of protection that fits your driving needs and vehicle usage.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {/* Partial Front */}
              <div className="bg-[#1A1A1A] border border-white/10 rounded-lg p-6 flex flex-col">
                <h3 className="text-xl font-heading font-bold text-foreground mb-3">
                  Partial Front
                </h3>
                <div className="text-accent text-sm font-medium mb-4">Entry Protection</div>
                
                <ul className="space-y-2 mb-6 flex-grow">
                  <li className="flex items-start gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                    Front bumper
                  </li>
                  <li className="flex items-start gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                    Partial hood (24")
                  </li>
                  <li className="flex items-start gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                    Mirror caps
                  </li>
                </ul>
                
                <p className="text-xs text-muted-foreground mb-4">
                  <strong>Ideal for:</strong> Short commutes, city driving, budget-conscious protection
                </p>
                
                <Button 
                  asChild
                  variant="outline" 
                  className="w-full border-accent text-accent hover:bg-accent hover:text-accent-foreground"
                >
                  <a href="#quote-form">Request Quote</a>
                </Button>
              </div>
              
              {/* Full Front */}
              <div className="bg-[#1A1A1A] border-2 border-accent rounded-lg p-6 flex flex-col relative">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground text-xs font-bold px-3 py-1 rounded-full uppercase">
                  Most Popular
                </div>
                
                <h3 className="text-xl font-heading font-bold text-foreground mb-3">
                  Full Front
                </h3>
                <div className="text-accent text-sm font-medium mb-4">Complete Front Protection</div>
                
                <ul className="space-y-2 mb-6 flex-grow">
                  <li className="flex items-start gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                    Full hood
                  </li>
                  <li className="flex items-start gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                    Front bumper
                  </li>
                  <li className="flex items-start gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                    Front fenders
                  </li>
                  <li className="flex items-start gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                    Headlights
                  </li>
                  <li className="flex items-start gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                    Mirror caps
                  </li>
                </ul>
                
                <p className="text-xs text-muted-foreground mb-4">
                  <strong>Ideal for:</strong> Daily drivers, highway commuters, I-15/215 travel
                </p>
                
                <Button 
                  asChild
                  className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-bold"
                >
                  <a href="#quote-form">Request Quote</a>
                </Button>
              </div>
              
              {/* Full Body */}
              <div className="bg-[#1A1A1A] border border-white/10 rounded-lg p-6 flex flex-col">
                <h3 className="text-xl font-heading font-bold text-foreground mb-3">
                  Full Body
                </h3>
                <div className="text-accent text-sm font-medium mb-4">Maximum Protection</div>
                
                <ul className="space-y-2 mb-6 flex-grow">
                  <li className="flex items-start gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                    All painted surfaces
                  </li>
                  <li className="flex items-start gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                    Door edges & cups
                  </li>
                  <li className="flex items-start gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                    Rocker panels
                  </li>
                  <li className="flex items-start gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                    Rear bumper
                  </li>
                </ul>
                
                <p className="text-xs text-muted-foreground mb-4">
                  <strong>Ideal for:</strong> Luxury/exotic vehicles, long-term ownership, maximum resale
                </p>
                
                <Button 
                  asChild
                  variant="outline" 
                  className="w-full border-accent text-accent hover:bg-accent hover:text-accent-foreground"
                >
                  <a href="#quote-form">Request Quote</a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          LAS VEGAS SPECIFIC PROOF
          Purpose: Localize urgency, not educate broadly
      ═══════════════════════════════════════════════════════════════════ */}
      <section className="py-16 bg-[#1A1A1A] border-y border-white/10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
                Why PPF is Critical in Las Vegas
              </h2>
              <p className="text-muted-foreground">
                The Mojave Desert is one of the harshest environments for automotive paint.
              </p>
            </div>
            
            <div className="grid sm:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-background rounded-lg border border-white/10">
                <Thermometer className="w-10 h-10 text-accent mx-auto mb-4" />
                <h3 className="font-heading font-bold text-foreground mb-2">Extreme UV Exposure</h3>
                <p className="text-sm text-muted-foreground">
                  300+ sunny days/year. UV rays oxidize and fade unprotected paint within 2-3 years.
                </p>
              </div>
              
              <div className="text-center p-6 bg-background rounded-lg border border-white/10">
                <MapPin className="w-10 h-10 text-accent mx-auto mb-4" />
                <h3 className="font-heading font-bold text-foreground mb-2">Highway Road Debris</h3>
                <p className="text-sm text-muted-foreground">
                  I-15, I-215, and US-95 are notorious for gravel, construction debris, and rock chips.
                </p>
              </div>
              
              <div className="text-center p-6 bg-background rounded-lg border border-white/10">
                <Car className="w-10 h-10 text-accent mx-auto mb-4" />
                <h3 className="font-heading font-bold text-foreground mb-2">Heat Impact</h3>
                <p className="text-sm text-muted-foreground">
                  Surface temps exceed 150°F in summer. Heat accelerates paint degradation without protection.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          BOTTOM CONVERSION REINFORCEMENT
          Purpose: Capture leads who scrolled, repeat core CTA
      ═══════════════════════════════════════════════════════════════════ */}
      <section id="quote-form" className="py-20 bg-gradient-to-b from-background to-[#1A1A1A]">
        <div className="container mx-auto px-4">
          <div className="max-w-lg mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Ready to Protect Your Vehicle?
            </h2>
            <p className="text-muted-foreground mb-8">
              Get a personalized quote for your vehicle. No pressure, no obligation.
            </p>
            
            <PPFQuoteForm variant="bottom" />
            
            <div className="mt-8 pt-8 border-t border-white/10">
              <p className="text-sm text-muted-foreground mb-2">Prefer to talk?</p>
              <a 
                href={phoneHref}
                className="inline-flex items-center gap-2 text-xl font-bold text-accent hover:text-accent/80 transition-colors"
              >
                <Phone className="w-5 h-5" />
                {phoneNumber}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Minimal Footer - No Navigation */}
      <footer className="py-6 bg-[#0F0F0F] border-t border-white/10">
        <div className="container mx-auto px-4 text-center">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} Battle Born Protection Films. Las Vegas, NV.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PPFQuoteLanding;
